<template lang="html">
  <div>
    <v-idle style='display: none'
      @idle="onIdle"
      :duration="60*60*8" />
    <div v-if='trialBannerOn' class='expiration-banner'>{{trialBannerMessageFormatted}}</div>
    <div class="main-layout">
      <div class="sidebar sidebar-fixed d-flex flex-column align-center" :class="{ collapsed: isSidebarCollapsed }">
        <div class="header-logo-container part">
          <router-link :to="{ name: dashboardRouteName }"  v-if="!isUnprivilegedUser">
            <company-logo v-if="whiteLabelingConfig.domain" :domain="whiteLabelingConfig.domain" view="dashboard"/>
            <div v-if="whiteLabelingConfig.domain" class="header-logo-container__text">
              {{$t('white.labeling.logo.text')}}
            </div>
            <component :is="activeLogoComponent" class="logo" v-else/>
          </router-link>
        </div>
        <div class="menu" v-if="isServiceProvider">
          <router-link :to="{ name: projectsRouteName }">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('activity.log')"
              icon="folder"
              :active="$route.name === projectsRouteName || $route.name === requestsRouteName"
            />
          </router-link>
          <router-link v-if='isServiceProviderAdmin' :to="{ name: serviceProviderConfigurationRouteName }">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('service.provider.manager.employees.management.title')"
              icon="people_alt"
              :active="$route.name === serviceProviderConfigurationRouteName"
            />
          </router-link>
        </div>
        <div class="menu" v-else>
          <router-link :to="{ name: dashboardRouteName }"  v-if="!isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('dashboard')"
              icon="dashboard"
              :active="$route.name === dashboardRouteName"
            />
          </router-link>
          <router-link :to="{ name: configurationRouteName }" v-if="!isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('configuration.title')"
              icon="tune"
              :active="$route.name === configurationRouteName"
              :rounded="true"
            />
          </router-link>
          <router-link :to="{ name: issuesRouteName }" v-if="!isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('issues.layout.title')"
              icon="bug_report"
              :active="$route.name === issuesRouteName"
            />
          </router-link>
          <router-link :to="{ name: insightsRouteName }" v-if="!isUnprivilegedUser">
              <main-menu-item
                :collapsed="isSidebarCollapsed"
                class="item"
                :title="$t('insights.layout.title')"
                icon="tips_and_updates"
                :active="$route.name === insightsRouteName" />
          </router-link>
          <router-link :to="{ name: workFlowsRouteName }"  v-if="experimentalFeatureOn && !isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              rounded
              :title="$t('workflows.header.title')"
              icon="alt_route"
              :active="$route.name === workFlowsRouteName"
            />
          </router-link>
          <router-link :to="{ name: CTCRouteName }"  v-if="!isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              rounded
              :title="$t('ctc.header.title')"
              icon="hub"
              :active="$route.name === CTCRouteName"
            />
          </router-link>
          <router-link :to="{ name: projectsRouteName }" v-if="!isUnprivilegedUser">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('activity.log')"
              icon="folder"
              :active="$route.name === projectsRouteName || $route.name === requestsRouteName"
            />
          </router-link>
          <router-link v-if="IsTenantAdmin" :to="{ name: settings }">
            <main-menu-item
              :collapsed="isSidebarCollapsed"
              class="item"
              :title="$t('settings.title')"
              icon="manage_accounts"
              :active="$route.name === settings"
            />
          </router-link>
        </div>

        <v-spacer />

        <span class="notifications" @click='toggleNotificationsWindow'>
          <main-menu-item
            :collapsed="isSidebarCollapsed"
            :title="$t('notifications.menu.item.title')"
            :rounded="true"
          >
            <template v-slot:icon>
              <div class="bell-badge-wrapper">
                <notifications-bell class="bell-icon"/>
                <span v-if="unreadNotifications.length" class="bell-badge">{{ unreadNotificationTooltipText }}</span>
              </div>
            </template>
          </main-menu-item>
        </span>

        <button
          class="sidebar-toggle"
          :style="{
            left: toggleLeft,
            backgroundColor: isSidebarCollapsed ? 'var(--brand-primary)' : '#ffffff',
            border: isSidebarCollapsed ? '2px solid var(--brand-primary)' : '2px solid #ffffff',
          }"
          @click="toggleSidebar"
          :aria-label="isSidebarCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')"
        >
          <v-icon :style="{ color: isSidebarCollapsed ? '#ffffff' : 'var(--brand-primary)' }">{{ isSidebarCollapsed ? 'chevron_right' : 'chevron_left' }}</v-icon>
        </button>

        <v-menu
          :close-on-content-click='true'
          rounded
          offset-x
          :nudge-width="25"
          bottom
          right
        >
          <template v-slot:activator="{ attrs, on }">
            <div class='profile'
                 v-bind="attrs"
                 v-on="on">
              <main-menu-item
                :collapsed="isSidebarCollapsed"
                v-if="profile"
                class="user"
              >
                <template v-slot:icon>
                  <img v-if="profile.avatarURL" :src="getProfileAvatar" class="avatar-image" />
                  <img v-else :src="require('@/assets/icons/noun_avatar_icn.svg')" class="avatar-image">
                </template>
                <template v-slot:title>
                  <div>{{ profile.firstName }}</div>
                  <div>{{ profile.lastName }}</div>
                </template>
              </main-menu-item>
            </div>
          </template>
            <v-list
              class="profile-menu"
            >
              <v-list-item
                v-for="(item, index) in menuItems"
                :key="index"
                link
                :to='{ name: item.routeName }'
              >
                <v-list-item-icon>
                  <v-icon v-text="item.icon"/>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="item.text" />
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="toggleDarkMode">
                <v-list-item-icon>
                  <v-icon>{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ isDarkMode ? $t('theme.light') : $t('theme.dark') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
        </v-menu>
        <router-link class="logout-link" :to="{ name: signOutRouteName }">{{ $t('logout') }}</router-link>
      </div>
      <div class="sidebar" :class="{ collapsed: isSidebarCollapsed }"></div>
      <div class="content-area flex-grow-1">
        <router-view />
      </div>
      <notifications-layout
        v-click-outside="closeNotificationsWindow"
        @closeNotificationsWindow='closeNotificationsWindow' :value='notificationsWindowShow'/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import vClickOutside from 'v-click-outside';
import { views } from '@/router/constants';
import store from '@/store';
import MainMenuItem from '@/components/MainMenuItem.vue';
import NotificationsLayout from '@/views/layouts/NotificationsLayout.vue';
import { LOAD_NOTIFICATIONS } from '@/store/notification/actionTypes';
import CynergyAppDashboard from '@/assets/icons/whitelabeling/cynergy.app/cynergy-logo-no-text-black.svg?inline';
import CynergyAppDashboardWhite from '@/assets/icons/whitelabeling/cynergy.app/cynergy-logo-no-text-white.svg?inline';
import CompanyLogo from '@/components/common/CompanyLogo.vue';
import NotificationsBell from '@/assets/notifications_bell.svg?inline';
import { signOut } from '@/amplify/auth';
import { SIDEBAR_WIDTH_CSS } from '@/constants/layout';
import { LOAD_LICENSING } from '@/store/user/actionTypes';

export default {
  name: 'MainLayout',
  components: {
    MainMenuItem,
    NotificationsLayout,
    CynergyAppDashboard,
    CynergyAppDashboardWhite,
    NotificationsBell,
    CompanyLogo,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  data() {
    return {
      notificationsWindowShow: false,
      currentTime: Date.now(),
      isSidebarCollapsed: false,
    };
  },
  created() {
    this.pollData();
    this.initTheme();
    this.$vuetify.theme.dark = this.isDarkMode;
    this.$store.dispatch(LOAD_LICENSING);
    this.banner = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);
  },
  watch: {
    isDarkMode(val) {
      this.$vuetify.theme.dark = val;
    },
  },
  beforeDestroy() {
    clearInterval(this.polling);
    clearInterval(this.banner);
  },
  methods: {
    async onIdle() {
      await signOut();
      await this.$router.push({ name: views.SIGN_IN });
    },
    async toggleNotificationsWindow() {
      this.notificationsWindowShow = !this.notificationsWindowShow;
    },
    async pollData() {
      this.polling = setInterval(async () => {
        await this.$store.dispatch(LOAD_NOTIFICATIONS);
      }, 600000);
    },
    closeNotificationsWindow() {
      this.notificationsWindowShow = false;
    },
    ...mapActions('theme', ['initTheme', 'toggleDarkMode']),
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },
  },
  computed: {
    ...mapGetters(['userInitials', 'profile', 'userRoleGroup', 'notifications', 'unreadNotifications', 'whiteLabelingConfig', 'licenseData']),
    ...mapGetters('theme', ['isDarkMode']),
    activeLogoComponent() {
      return this.isDarkMode ? 'CynergyAppDashboardWhite' : 'CynergyAppDashboard';
    },

    menuItems() {
      return [
        {
          text: this.$t('profile.menu.option'),
          routeName: this.profileRouteName,
          icon: 'person',
        },
        {
          text: this.$t('integrations.menu.option'),
          routeName: this.integrationsRouteName,
          icon: 'settings',
        },
        {
          text: this.$t('authentications.menu.option'),
          routeName: this.authenticationsRouteName,
          icon: 'vpn_key',
        },
        {
          text: this.$t('reports.menu.option'),
          routeName: this.reportsRouteName,
          icon: 'summarize',
        },
      ];
    },
    experimentalFeatureOn() {
      return this.$store.getters.hasLicenseModule('experimental');
    },
    trialBannerOn() {
      if (this.isTrialExpired) return true;
      if (!this.licenseData?.license?.expiresAt) return false;
      if (this.licenseData.license.licenseType !== 'pov_poc') return false;
      return this.licenseData.daysUntilExpiry <= 30;
    },
    isTrialExpired() {
      return this.$store.getters.isLicenseExpired;
    },
    trialBannerMessageFormatted() {
      if (this.isTrialExpired) return 'Your license has expired';
      if (!this.licenseData?.license?.expiresAt) return '';

      const now = Math.floor(this.currentTime / (60 * 1000)) * 60 * 1000;
      const targetDate = new Date(this.licenseData.license.expiresAt);
      const diffInMs = targetDate.getTime() - now;
      if (diffInMs < 0) return 'Your license has expired';

      let totalMinutes = diffInMs / (1000 * 60);
      const days = Math.floor(totalMinutes / (24 * 60));
      totalMinutes %= (24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.floor(totalMinutes % 60);

      if (days > 0) return `Your trial ends in ${days} days`;
      // eslint-disable-next-line prefer-template
      return `Your trial ends in ${hours}:${minutes >= 10 ? minutes : '0' + minutes} hours`;
    },
    unreadNotificationTooltipText() {
      if (this.unreadNotifications.length >= 100) {
        return '99+';
      }
      return this.unreadNotifications.length;
    },
    profileRouteName() {
      const roles = this.profile?.roles?.map(role => role.name) || [];
      if (roles.includes('SPWorker') ||
        roles.includes('SPAdmin') ||
        roles.includes('SPFreelancer')) {
        return views.SERVICE_PROVIDER_PROFILE;
      }
      return views.CUSTOMER_PROFILE;
    },
    getProfileAvatar() {
      return `${this.profile?.avatarURL}?${Date.now().toString()}`;
    },
    projectsRouteName: () => views.ACTIVITY_LOG,
    requestsRouteName: () => views.REQUESTS,
    dashboardRouteName: () => views.DASHBOARD,
    settings: () => views.SETTINGS,
    issuesRouteName: () => views.ISSUES,
    insightsRouteName: () => views.INSIGHTS,
    configurationRouteName: () => views.INVENTORY,
    signOutRouteName: () => views.SIGN_OUT,
    workFlowsRouteName: () => views.PLAYBOOKS,
    CTCRouteName: () => views.CTC,
    integrationsRouteName: () => views.INTEGRATIONS,
    authenticationsRouteName: () => views.AUTHENTICATIONS,
    reportsRouteName: () => views.REPORTS,
    serviceProviderConfigurationRouteName: () => views.SERVICE_PROVIDER_CONFIGURATION,
    IsTenantAdmin: () => store.getters.permissions?.includes('all.tenant.users') || false,
    isServiceProvider: () => store.getters.permissions?.includes('company.sp') || false,
    isServiceProviderAdmin: () => store.getters.permissions?.includes('company.sp.admin') || false,
    isUnprivilegedUser: () => !store.getters.permissions?.length,
    // drives the toggle button's left — matches sidebar transition so it animates smoothly
    toggleLeft() {
      // button is 18px wide; center it on the sidebar's right edge
      const halfBtn = '9px';
      const expandedEdge = SIDEBAR_WIDTH_CSS;
      const collapsedEdge = '12px';
      const edge = this.isSidebarCollapsed ? collapsedEdge : expandedEdge;
      return `calc(${edge} - ${halfBtn})`;
    },
  },

};
</script>
<style lang='scss'>
@import '~@/assets/styles/design-system';
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.expiration-banner{
  height: rem(40);
  background-color: var(--brand-secondary);
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: $font-family-bold;
  font-size: larger;
  color: $white;
}
// badge override moved to scoped :deep
// .v-badge__badge { ... } removed from global scope

.profile-menu {
  min-width: rem(220);

  :deep(.v-list-item) {
    padding-left: $space-3;
  }

  :deep(.v-list-item__title) {
    font-family: $font-family-medium;
  }

  :deep(.v-list-item__icon) {
    margin-top: $space-2;
    margin-bottom: 0;
    margin-right: $space-4;
  }
}

:deep(.theme--light.v-list) {
  width: rem(150);
}

:deep(.v-list-item) {
  min-height: rem(30);
  padding-right: $space-8;
  padding-left: $space-8;
}

.list-group-title {
  padding-left: $space-4;
}

.list-sub-group-item {
  padding-left: $space-4;
}

:deep(.v-list-item__title) {
  font-family: $font-family-medium;
  font-size: $font-size-sm;
}

:deep(.v-list .v-list-item--active) {
  color: var(--brand-primary);
}

.avatar-image {
  width: unquote("min(#{$space-14}, 5vh)");
  height: unquote("min(#{$space-14}, 5vh)");
  min-width: unquote("min(#{$space-14}, 5vh)");
  min-height: unquote("min(#{$space-14}, 5vh)");
  max-width: unquote("min(#{$space-14}, 5vh)");
  max-height: unquote("min(#{$space-14}, 5vh)");
  border-radius: 50%;
  border: 1px solid transparent;

  :deep(.v-image) {
    max-height: 100%;
    max-width: 100%;
  }
}

.bell-icon {
  color: var(--sidebar-text);
  // match nav icon size in MainMenuItem
  width: unquote("min(#{$space-8}, 3.2vh)");
  height: unquote("min(#{$space-8}, 3.2vh)");
  min-width: unquote("min(#{$space-8}, 3.2vh)");
  min-height: unquote("min(#{$space-8}, 3.2vh)");
  max-width: unquote("min(#{$space-8}, 3.2vh)");
  max-height: unquote("min(#{$space-8}, 3.2vh)");

  :deep(path) {
    fill: var(--sidebar-text);
  }
}

.bell-badge-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .bell-badge {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(35%, -35%);
    background: #FF1A60;
    color: white;
    font-size: unquote("min(#{$font-size-xs}, 1.1vh)");
    height: unquote("min(#{$space-5}, 1.7vh)");
    min-width: unquote("min(#{$space-5}, 1.7vh)");
    line-height: unquote("min(#{$space-5}, 1.7vh)");
    padding: 0 unquote("min(#{$space-1}, 0.35vh)");
    border-radius: 25%;
    white-space: nowrap;
    box-sizing: content-box;
    text-align: center;
    pointer-events: none;
  }
}

.main-layout {
  display: flex;
}

.sidebar-fixed {
  position: fixed;
  z-index: 2000;
}

.sidebar {
  background: var(--sidebar-bg);
  fill: var(--sidebar-text);
  font-family: $font-family-medium;
  font-weight: 500;
  font-size: $font-size-base;
  letter-spacing: 0;
  box-shadow: var(--sidebar-edge-shadow);
  line-height: $font-size-base;
  height: 100vh;
  left: 0;
  padding-bottom: unquote("min(#{$space-6}, 2vh)");
  text-align: center;
  top: 0;
  // vw-based width — stays fixed regardless of browser zoom (px scales with zoom, vw doesn't)
  width: $sidebar-width-vw;
  min-width: $sidebar-width-vw;
  max-width: $sidebar-width-vw;
  overflow: hidden;
  transition: width 0.3s ease, min-width 0.3s ease;

  // Moved badge override here
  :deep(.v-badge__badge) {
    border-radius: $radius-md;
  }

  &.collapsed {
    width: rem(12);
    min-width: rem(12);
    background-color: transparent;
    box-shadow: none;

    // Hide all internal content when collapsed
    .menu,
    .header-logo-container,
    .logout-link,
    .notifications,
    .v-spacer,
    .v-menu,
    .badge,
    .profile {
      display: none;
    }
  }
}

.sidebar-toggle {
  // fixed so it stays visible; left is driven by JS computed to animate with the sidebar.
  // Size and vertical position are fixed px so they never scale with screen size.
  position: fixed;
  bottom: 48px;
  transform: translateY(0);
  transition: left 0.3s ease;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  box-shadow: $shadow-md;

  :deep(.v-icon) {
    font-size: 12px;
  }
}

.menu .item {
  margin: unquote("min(#{$space-1}, 0.3vh)") 0;
  display: block; // router-link is inline by default; block makes width: 100% work correctly
}

.user {
  margin-top: unquote("min(#{$space-2}, 0.5vh)");
  margin-bottom: unquote("min(#{$space-4}, 0.8vh)");
  // allow name to wrap — first/last name are intentionally on separate lines
  :deep(.full-name) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
  &:hover {
    border-color: var(--sidebar-bg);
  }
}

.notifications {
  // Small fixed margin — don't use 30% which causes overflow at high zoom
  margin-top: $space-2;

  .main-menu-item {
    &:hover {
      border-color: var(--sidebar-bg);
    }
  }

  &:hover {
    cursor: pointer;
  }
}

.logout-link {
  margin-top: unquote("min(#{$space-3}, 0.8vh)");
  font-family: $font-family-medium;
  font-size: unquote("min(#{$font-size-xs}, 1.3vh)");
  line-height: unquote("min(#{$font-size-sm}, 1.6vh)");
  letter-spacing: rem(1.07);
  text-transform: uppercase;
  text-decoration: none;
  color: var(--sidebar-active);
}

// Hide sidebar labels at very small heights — items already scale via min() in MainMenuItem
@media (max-height: 620px) {
  .sidebar {
    padding-bottom: $space-1;
  }
}

.content-area {
  background-color: var(--bg-primary);
  position: relative;
  z-index: 5;
  width: 100%;
  overflow-x: auto;
}

.header-logo-container {
  display: flex;
  justify-content: center;
  margin-top: unquote("max(#{$space-4}, min(#{$space-6}, 3vh))");
  max-height: unquote("min(#{rem(75)}, 7vh)");

  .logo {
    height: unquote("min(#{rem(75)}, 7vh)");
    width: unquote("min(#{rem(75)}, 7vh)");
    margin-top: rem(-10);
  }

  &__text {
    margin-top: rem(-8);
    font-family: $font-family-semibold;
    font-size: $font-size-2xs; // 8px
    color: var(--text-primary);
  }
}

.badge-container {
  position: relative;
  display: inline-block;
  margin: rem(-15) 0 rem(-15);
}

</style>
